import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Tooltip,
  Avatar,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Popover,
  CircularProgress,
} from '@mui/material';
import { authFetch } from '../../../utils/authHelpers';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { leesJson } from '../../../utils/apiFout';
import { computeDayLoad, pctOfDay, DEFAULT_CAPACITY_RULES, DAGSTAAT } from '../../../utils/capacityCalc';
import { getCapacityCellStyle } from '../../../utils/capacityColors';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { T, Icon, stitchInputSx, stitchPill, stitchSectionLabel, stitchGradientBtn, stitchOutlineBtn, stitchDialogPaper, stitchTooltipSlotProps, StitchIconBtn } from './stitch';
import { taskTypeColors, formatDate, getInitials, stringToColor } from './projectMeta';
import InlineTextEdit from './InlineTextEdit';
import WijzigingslogKnop from '../../../ui-components/WijzigingslogKnop';


// Task Detail Dialog Component
// PrioNr is een singleSelect in Airtable — deze lijst moet exact de keuzes van dat
// veld volgen, anders weigert Airtable de update ("insufficient permissions to
// create new select option").
const prioOptions = [
  { value: 'Prio 1', label: 'Prio 1', color: '#ef4444' },
  { value: 'Prio 2', label: 'Prio 2', color: '#f97316' },
  { value: 'Prio 3', label: 'Prio 3', color: '#eab308' },
  { value: 'Prio 4', label: 'Prio 4', color: '#84cc16' },
  { value: 'Geen prio', label: 'Geen prio', color: '#3b82f6' },
  { value: 'Deadline', label: 'Deadline', color: '#dc2626' },
  { value: 'Wacht op feedback', label: 'Wacht op feedback', color: '#f59e0b' },
  { value: 'Nog niet van toepassing', label: 'Nog niet van toepassing', color: '#14b8a6' },
  { value: 'Vanallesuurtje', label: 'Vanallesuurtje', color: '#06b6d4' },
  { value: 'Afspraak', label: 'Afspraak', color: '#8b5cf6' },
  { value: 'Draaidag', label: 'Draaidag', color: '#f59e0b' },
  { value: 'Vrij/intern', label: 'Vrij/intern', color: '#ec4899' },
  { value: 'Intern', label: 'Intern', color: '#a855f7' },
  { value: 'Afgerond', label: 'Afgerond', color: '#10b981' },
  { value: 'Opgestuurd', label: 'Opgestuurd', color: '#6366f1' },
  { value: 'Gearchiveerd', label: 'Gearchiveerd', color: '#9ca3af' },
];


// Keuzes van het singleSelect-veld `Type` op de Tasks-tabel. ('1.0' en 'Isa' staan
// er ook in, maar zijn restanten van een oude import — niet aanbieden.)
const taskTypeOptions = [
  'Preproductie', 'Draaidag', 'Montage', 'Animatie', 'Deadline', 'Vrije dag',
  'Intern', 'Illustratie', 'Projectafronding', 'Archief', 'IT', 'Compliance',
  'Reparatie', 'Marketing', 'Social Strategie', 'Afspraak', 'Regie',
];


// Keuzes van `TypeDeadline`.
const deadlineTypeOptions = ['Hard', 'Klant', 'Zacht', 'Intern'];


// Get color for a prio value
const getPrioColor = (prioValue) => {
  const option = prioOptions.find(o => o.value === prioValue);
  return option?.color || '#9ca3af';
};



// 📅 Mini-agenda naast de uitvoerder: hoe vol staan de komende twee weken, en
// waar valt deze taak? Zelfde kleuren als de planner-capaciteitsstrook
// (Joris, 1 sep). Data: person-tasks-range (taken + kalenderblokken).
function AssigneeAgenda({ personId, taakStart, taakEind }) {
  const [anchor, setAnchor] = useState(null);
  const [dagen, setDagen] = useState(null);
  const [fout, setFout] = useState(false);

  const openen = async (e) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
    if (dagen || !personId) return;
    try {
      const dayISOs = [];
      const d = new Date();
      while (dayISOs.length < 10) {
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) {
          dayISOs.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
        }
        d.setDate(d.getDate() + 1);
      }
      const params = `startDate=${dayISOs[0]}&endDate=${dayISOs[dayISOs.length - 1]}&personIds[]=${encodeURIComponent(personId)}`;
      const r = await authFetch(`${BACKEND_URL}/Api/person-tasks-range?${params}`);
      const data = await leesJson(r, 'Werkdruk laden');
      const { uren, staat } = computeDayLoad(data[personId] || [], dayISOs, {}, DEFAULT_CAPACITY_RULES);
      setDagen({ dayISOs, uren, staat });
    } catch {
      setFout(true);
    }
  };

  const inTaak = (iso) => taakStart && iso >= taakStart && iso <= (taakEind || taakStart);
  const DAGNAMEN = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

  return (
    <>
      <Tooltip title="Agenda van de uitvoerder" arrow slotProps={stitchTooltipSlotProps}>
        <Box component="span" onClick={openen}
          sx={{ display: 'inline-flex', color: T.outlineVariant, cursor: 'pointer', ml: 0.25,
                '&:hover': { color: T.primary } }}>
          <Icon name="calendar_month" size={17} />
        </Box>
      </Tooltip>
      <Popover open={Boolean(anchor)} anchorEl={anchor}
        onClose={(e) => { e?.stopPropagation?.(); setAnchor(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { onClick: (e) => e.stopPropagation(), sx: { p: 1.5, borderRadius: '0.75rem' } } }}>
        {fout ? (
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.outline }}>Agenda laden mislukt</Typography>
        ) : !dagen ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
            <CircularProgress size={14} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.outline }}>Agenda laden…</Typography>
          </Box>
        ) : (
          <Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: T.outline, mb: 0.75 }}>
              Komende 10 werkdagen · rand = deze taak
            </Typography>
            <Box sx={{ display: 'flex', gap: '3px' }}>
              {dagen.dayISOs.map((iso) => {
                const isDraaidag = dagen.staat[iso] === DAGSTAAT.DRAAIDAG;
                const vrij = dagen.staat[iso] === DAGSTAAT.VRIJ;
                const uren = dagen.uren[iso] || 0;
                const pct = pctOfDay(uren, DEFAULT_CAPACITY_RULES.hoursPerDay);
                const stijl = vrij
                  ? { bg: '#f1f5f9', fg: '#94a3b8' }
                  : isDraaidag
                    ? { bg: '#fde68a', fg: '#92400e' }
                    : getCapacityCellStyle(pct);
                const dt = new Date(`${iso}T12:00:00`);
                return (
                  <Box key={iso} sx={{
                    width: 44, borderRadius: '0.4rem', px: 0.25, py: 0.5, textAlign: 'center',
                    bgcolor: stijl.bg || '#f1f5f9',
                    border: inTaak(iso) ? `2px solid ${T.primary}` : '2px solid transparent',
                  }}>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outline }}>
                      {DAGNAMEN[dt.getDay()]} {dt.getDate()}/{dt.getMonth() + 1}
                    </Typography>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700,
                                      color: stijl.fg || stijl.text || T.text }}>
                      {vrij ? 'vrij' : isDraaidag ? 'draai' : `${Math.round(uren * 10) / 10}u`}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Popover>
    </>
  );
}


function TaskDetailDialog({ task, open, onClose, onUpdate, onDelete, autoEditDate = false, personen = [] }) {
  const navigate = useNavigate();
  // Draaidag-taak → direct een callsheet maken (Joris, 15 sep). De callsheet-lijst zoekt de
  // draaidag op naam (taaknaam = draaidagnaam uit Outlook) binnen dit project.
  const isDraaidagTaak = task?.fields?.Type === 'Draaidag' || task?.fields?.PrioNr === 'Draaidag';
  const naarCallsheet = () => {
    const pid = Array.isArray(task?.fields?.Project) ? task.fields.Project[0] : '';
    const q = new URLSearchParams({ new: '1', naam: task?.fields?.NaamTaak || '' });
    if (pid) q.set('project', pid);
    onClose?.();
    navigate(`/callsheets?${q.toString()}`);
  };
  const [verwijderStap, setVerwijderStap] = useState(0); // 0 = knop, 1 = bevestigen, 2 = bezig
  useEffect(() => { setVerwijderStap(0); }, [task?.id, open]);
  const [editingPrio, setEditingPrio] = useState(false);
  const [editingType, setEditingType] = useState(false);
  const [editingAssignee, setEditingAssignee] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState(null); // 'startDate', 'endDate', 'deadline'
  const [calendarViewDate, setCalendarViewDate] = useState(new Date()); // For navigating calendar

  // Reset edit state when dialog closes, auto-edit date when opening from timeline
  useEffect(() => {
    if (!open) {
      setEditingField(null);
      setEditingPrio(false);
      setEditingType(false);
      setEditingAssignee(false);
    } else if (autoEditDate && task?.fields) {
      // Auto-open date editor - prefer startDate, fallback to deadline
      if (task.fields.StartDateUitvoerder) {
        setEditingField('startDate');
      } else if (task.fields.Deadline) {
        setEditingField('deadline');
      } else {
        setEditingField('startDate'); // Allow setting new date
      }
    }
  }, [open, autoEditDate, task?.fields]);

  // Set calendar view date when starting to edit a date field
  useEffect(() => {
    if (editingField && task?.fields) {
      const dateStr = editingField === 'startDate' ? task.fields.StartDateUitvoerder :
                     editingField === 'endDate' ? task.fields.EndDateUitvoerder :
                     task.fields.Deadline;
      if (dateStr) {
        try {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            setCalendarViewDate(date);
          }
        } catch (e) {
          // Invalid date, keep current view date
        }
      }
    }
  }, [editingField, task?.fields]);

  if (!task) return null;

  const name = task.fields?.NaamTaak || 'Onbekende taak';
  const type = task.fields?.Type || '';
  const prio = task.fields?.PrioNr || '';
  const isComplete = task.fields?.Afgerond === true;

  const handlePrioChange = async (newPrio) => {
    if (!onUpdate || newPrio === prio) {
      setEditingPrio(false);
      return;
    }

    setSaving(true);
    try {
      await onUpdate(task.id, { PrioNr: newPrio });
      setEditingPrio(false);
    } catch (err) {
      console.error('Failed to update prio:', err);
      toast.error('Fout bij bijwerken prioriteit');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteToggle = async () => {
    if (!onUpdate) return;
    setSaving(true);
    try {
      await onUpdate(task.id, { Afgerond: !isComplete });
    } finally {
      setSaving(false);
    }
  };

  // Eén generieke opslag-route voor alle vrij bewerkbare velden. `onUpdate` gooit
  // niet, maar geeft false terug bij een mislukte PATCH (en toont zelf al een
  // foutmelding), dus alleen bij true een bevestiging tonen.
  const handleFieldSave = async (fieldName, waarde) => {
    if (!onUpdate) return;
    setSaving(true);
    try {
      const gelukt = await onUpdate(task.id, { [fieldName]: waarde });
      if (gelukt) toast.success('Opgeslagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDateChange = async (fieldName, newDate) => {
    if (!onUpdate || !newDate) return;

    // Lokale datum, niet UTC: toISOString() schuift in CEST een dag terug.
    const dateStr = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}`;
    setSaving(true);
    try {
      const gelukt = await onUpdate(task.id, { [fieldName]: dateStr });
      setEditingField(null);
      if (gelukt) toast.success('Datum bijgewerkt');
    } catch (err) {
      console.error('Failed to update date:', err);
      toast.error('Fout bij bijwerken datum');
    } finally {
      setSaving(false);
    }
  };

  // Dates
  const startDate = task.fields?.StartDateUitvoerder;
  const endDate = task.fields?.EndDateUitvoerder;
  const deadline = task.fields?.Deadline;
  const deadlineType = task.fields?.TypeDeadline || '';
  const created = task.fields?.Created;

  // Duration
  const urenWerk = task.fields?.UrenWerk;
  const uren = task.fields?.Uren;

  // Assignee
  const assigneeNames = task.fields?.['Voornaam Assignee'] || [];
  const assignee = Array.isArray(assigneeNames)
    ? assigneeNames.filter(n => n && !String(n).startsWith('rec'))
    : (assigneeNames ? [assigneeNames] : []);
  const functie = task.fields?.['Functie (from Assignee)'] || [];
  // Gekoppelde record-IDs (het schrijfbare veld); de namen hierboven zijn lookups.
  const assigneeIds = Array.isArray(task.fields?.Assignee) ? task.fields.Assignee : [];
  const assigneeSelectie = assigneeIds
    .map(id => personen.find(p => p.id === id))
    .filter(Boolean);

  // Notes
  const opmerking = task.fields?.Opmerking || '';

  // Subtasks
  const subtasks = task.fields?.['Sub tasks'] || [];
  const hasOpenSubtasks = task.fields?.['Open subtaken'];

  // Calculate duration in days
  const durationDays = startDate && endDate
    ? Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { ...stitchDialogPaper, overflow: 'hidden' }
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 3,
        pb: 2,
        background: `linear-gradient(135deg, ${taskTypeColors[type] || '#6366f1'}15 0%, ${T.surface} 100%)`,
        borderBottom: `1px solid ${T.surfaceHigh}`
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              {/* Editable Type Selector */}
              {editingType ? (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  {taskTypeOptions.map((opt) => (
                    <Typography
                      key={opt}
                      component="span"
                      onClick={async () => {
                        setEditingType(false);
                        if (opt !== type) await handleFieldSave('Type', opt);
                      }}
                      sx={{
                        ...stitchPill(opt === type ? '#fff' : T.text, opt === type ? (taskTypeColors[opt] || T.primary) : T.surfaceLow),
                        cursor: 'pointer',
                        '&:hover': { bgcolor: taskTypeColors[opt] || T.surfaceHigh, color: '#fff' },
                      }}
                    >
                      {opt}
                    </Typography>
                  ))}
                  <StitchIconBtn onClick={() => setEditingType(false)} sx={{ width: 24, height: 24 }}>
                    <Icon name="close" size={14} />
                  </StitchIconBtn>
                </Box>
              ) : (
                <Typography
                  component="span"
                  onClick={() => onUpdate && setEditingType(true)}
                  sx={{
                    ...stitchPill(type ? '#fff' : T.outline, type ? (taskTypeColors[type] || T.primary) : T.surfaceLow),
                    cursor: onUpdate ? 'pointer' : 'default',
                    border: `1px solid ${type ? 'transparent' : T.surfaceHigh}`,
                    '&:hover': onUpdate ? { opacity: 0.9 } : {},
                  }}
                >
                  {onUpdate ? '✏️ ' : ''}{type || 'Geen type'}
                </Typography>
              )}
              {/* Editable Prio Selector */}
              {editingPrio ? (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  {prioOptions.map((opt) => (
                    <Typography
                      key={opt.value}
                      component="span"
                      onClick={() => handlePrioChange(opt.value)}
                      sx={{
                        ...stitchPill(opt.value === prio ? '#fff' : T.text, opt.value === prio ? opt.color : T.surfaceLow),
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: opt.value === prio ? opt.color : T.surfaceHigh,
                          opacity: opt.value === prio ? 0.9 : 1,
                        },
                      }}
                    >
                      {opt.label}
                    </Typography>
                  ))}
                  <StitchIconBtn onClick={() => setEditingPrio(false)} sx={{ width: 24, height: 24 }}>
                    <Icon name="close" size={14} />
                  </StitchIconBtn>
                </Box>
              ) : (
                <Typography
                  component="span"
                  onClick={() => setEditingPrio(true)}
                  sx={{
                    ...stitchPill(
                      prio ? getPrioColor(prio) : T.outline,
                      prio ? `${getPrioColor(prio)}20` : T.surfaceLow
                    ),
                    cursor: 'pointer',
                    border: `1px solid ${prio ? getPrioColor(prio) : T.surfaceHigh}`,
                    '&:hover': { bgcolor: prio ? `${getPrioColor(prio)}30` : T.surfaceHigh },
                  }}
                >
                  {'\u270F\uFE0F'} {prio || 'Geen prio'}
                </Typography>
              )}
              {/* Clickable completion toggle */}
              <Typography
                component="span"
                onClick={handleCompleteToggle}
                sx={{
                  ...stitchPill(
                    isComplete ? '#166534' : T.outline,
                    isComplete ? '#dcfce7' : T.surfaceLow
                  ),
                  cursor: saving ? 'default' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                  '&:hover': {
                    bgcolor: isComplete ? '#bbf7d0' : T.surfaceHigh,
                  },
                }}
              >
                {isComplete ? "\u2713 Afgerond" : "\u25CB Nog niet af"}
              </Typography>
            </Box>
            {onUpdate ? (
              <InlineTextEdit
                value={task.fields?.NaamTaak || ''}
                placeholder="Taaknaam invullen"
                onSave={(v) => handleFieldSave('NaamTaak', v)}
                displaySx={{ fontFamily: T.font, fontWeight: 700, fontSize: '1.15rem', lineHeight: 1.3 }}
              />
            ) : (
              <Typography variant="h6" sx={{ fontFamily: T.font, fontWeight: 700, color: T.text, lineHeight: 1.3 }}>
                {name}
              </Typography>
            )}
          </Box>
          <StitchIconBtn onClick={onClose} sx={{ ml: 1 }}>
            <Icon name="close" size={20} />
          </StitchIconBtn>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Assignee Section */}
        {(assignee.length > 0 || onUpdate) && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ ...stitchSectionLabel, mb: 1, display: 'block' }}>
              UITVOERDER
            </Typography>
            {editingAssignee ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Autocomplete
                  multiple
                  fullWidth
                  size="small"
                  options={personen}
                  value={assigneeSelectie}
                  getOptionLabel={(o) => o?.fields?.['Volledige naam'] || o?.fields?.Naam || ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  onChange={async (_e, nieuw) => {
                    setEditingAssignee(false);
                    await handleFieldSave('Assignee', nieuw.map(p => p.id));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} autoFocus placeholder="Kies uitvoerder(s)" sx={stitchInputSx} />
                  )}
                />
                <StitchIconBtn onClick={() => setEditingAssignee(false)} title="Annuleren">
                  <Icon name="close" size={18} />
                </StitchIconBtn>
              </Box>
            ) : (
              <Tooltip title={onUpdate ? 'Klik om te wijzigen' : ''} arrow slotProps={stitchTooltipSlotProps}>
                <Box
                  onClick={() => onUpdate && setEditingAssignee(true)}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    cursor: onUpdate ? 'pointer' : 'default',
                    borderRadius: '0.5rem',
                    p: 1,
                    m: -1,
                    minHeight: 40,
                    alignItems: 'center',
                    '&:hover': onUpdate ? { bgcolor: T.surfaceLow } : {},
                  }}
                >
                  {assignee.length === 0 ? (
                    <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline, fontStyle: 'italic' }}>
                      Nog niemand toegewezen
                    </Typography>
                  ) : assignee.map((name, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: stringToColor(name) }}>
                        {getInitials(name)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600 }}>{name}</Typography>
                        {functie[idx] && (
                          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>{functie[idx]}</Typography>
                        )}
                      </Box>
                      {assigneeIds[idx] && (
                        <AssigneeAgenda personId={assigneeIds[idx]} taakStart={startDate} taakEind={endDate} />
                      )}
                    </Box>
                  ))}
                </Box>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Dates Section */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ ...stitchSectionLabel, mb: 1.5, display: 'block' }}>
            PLANNING
          </Typography>
          {/* Alle drie de tegels staan er altijd — ook zonder waarde, anders kun je
              een lege datum nooit invullen (was de klacht uit de feedback). */}
          <Grid container spacing={2}>
            {[
              { key: 'startDate', label: 'Startdatum', waarde: startDate, accent: T.primary, bg: T.surfaceLow, hover: T.surfaceHigh },
              { key: 'endDate', label: 'Einddatum', waarde: endDate, accent: T.primary, bg: T.surfaceLow, hover: T.surfaceHigh },
              {
                key: 'deadline',
                label: `Deadline${deadlineType ? ` (${deadlineType})` : ''}`,
                waarde: deadline,
                accent: T.amber,
                bg: deadline ? T.amberBg : T.surfaceLow,
                hover: deadline ? '#fde68a' : T.surfaceHigh,
              },
            ].filter(tegel => onUpdate || tegel.waarde).map(tegel => (
              <Grid item xs={6} sm={4} key={tegel.key}>
                <Tooltip title={onUpdate ? (tegel.waarde ? 'Klik om te bewerken' : 'Klik om in te vullen') : ''} arrow slotProps={stitchTooltipSlotProps}>
                  <Box
                    onClick={() => onUpdate && setEditingField(editingField === tegel.key ? null : tegel.key)}
                    sx={{
                      p: 1.5,
                      backgroundColor: editingField === tegel.key ? T.primaryContainer : tegel.bg,
                      borderRadius: '0.75rem',
                      textAlign: 'center',
                      cursor: onUpdate ? 'pointer' : 'default',
                      transition: 'all 0.2s',
                      border: editingField === tegel.key ? `2px solid ${tegel.accent}` : '2px solid transparent',
                      '&:hover': onUpdate ? { backgroundColor: tegel.hover } : {},
                    }}
                  >
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }} display="block">
                      {tegel.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: T.fontBody,
                        fontWeight: 600,
                        color: tegel.waarde ? T.text : T.outline,
                        fontStyle: tegel.waarde ? 'normal' : 'italic',
                      }}
                    >
                      {tegel.waarde ? formatDate(tegel.waarde) : 'Niet ingevuld'}
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            ))}
          </Grid>

          {/* Soort deadline — alleen relevant zodra er een deadline staat */}
          {onUpdate && deadline && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, mr: 0.5 }}>
                Soort deadline:
              </Typography>
              {deadlineTypeOptions.map(opt => (
                <Typography
                  key={opt}
                  component="span"
                  onClick={() => handleFieldSave('TypeDeadline', opt === deadlineType ? null : opt)}
                  sx={{
                    ...stitchPill(opt === deadlineType ? '#fff' : T.text, opt === deadlineType ? T.amber : T.surfaceLow),
                    cursor: 'pointer',
                    '&:hover': { bgcolor: opt === deadlineType ? T.amber : T.surfaceHigh },
                  }}
                >
                  {opt}
                </Typography>
              ))}
            </Box>
          )}

          {/* Inline DatePicker when editing */}
          {editingField && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ p: 2, borderRadius: '0.75rem', boxShadow: 3, bgcolor: T.surface }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
                    {editingField === 'startDate' ? 'Startdatum wijzigen' :
                     editingField === 'endDate' ? 'Einddatum wijzigen' : 'Deadline wijzigen'}
                  </Typography>
                  <Box
                    component="span"
                    onClick={() => setCalendarViewDate(new Date())}
                    sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Vandaag
                  </Box>
                </Box>
                <DatePicker
                  selected={
                    editingField === 'startDate' && startDate ? new Date(startDate) :
                    editingField === 'endDate' && endDate ? new Date(endDate) :
                    editingField === 'deadline' && deadline ? new Date(deadline) :
                    null
                  }
                  onChange={(date) => {
                    const fieldName = editingField === 'startDate' ? 'StartDateUitvoerder' :
                                     editingField === 'endDate' ? 'EndDateUitvoerder' : 'Deadline';
                    handleDateChange(fieldName, date);
                  }}
                  openToDate={calendarViewDate}
                  onMonthChange={(date) => setCalendarViewDate(date)}
                  inline
                />
                <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Box component="span" onClick={() => setEditingField(null)} sx={{ ...stitchOutlineBtn, fontSize: '0.75rem', px: 1.5 }}>Annuleren</Box>
                  <Box
                    component="span"
                    onClick={async () => {
                      const fieldName = editingField === 'startDate' ? 'StartDateUitvoerder' :
                                       editingField === 'endDate' ? 'EndDateUitvoerder' : 'Deadline';
                      setEditingField(null);
                      await handleFieldSave(fieldName, null);
                    }}
                    sx={{ ...stitchOutlineBtn, fontSize: '0.75rem', px: 1.5, color: T.error, borderColor: T.error }}
                  >
                    Wissen
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Duration Section */}
        {(urenWerk || durationDays || onUpdate) && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ ...stitchSectionLabel, mb: 1.5, display: 'block' }}>
              TIJDSINSCHATTING
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {onUpdate ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
                  <Icon name="schedule" size={18} sx={{ color: T.outline }} />
                  <InlineTextEdit
                    value={urenWerk === null || urenWerk === undefined ? '' : Math.round(urenWerk * 10) / 10}
                    numeric
                    suffix=" uur werk"
                    placeholder="Uren invullen"
                    onSave={(v) => handleFieldSave('UrenWerk', v)}
                  />
                </Box>
              ) : urenWerk ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon name="schedule" size={18} sx={{ color: T.outline }} />
                  <Typography variant="body2" sx={{ fontFamily: T.fontBody }}>
                    <strong>{Math.round(urenWerk * 10) / 10}</strong> uur werk
                  </Typography>
                </Box>
              ) : null}
              {durationDays && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon name="calendar_today" size={18} sx={{ color: T.outline }} />
                  <Typography variant="body2" sx={{ fontFamily: T.fontBody }}>
                    <strong>{durationDays}</strong> {durationDays === 1 ? 'dag' : 'dagen'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Subtasks indicator */}
        {subtasks.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ ...stitchSectionLabel, mb: 1, display: 'block' }}>
              SUBTAKEN
            </Typography>
            <Typography component="span" sx={{
              ...stitchPill(
                hasOpenSubtasks ? '#92400e' : '#166534',
                hasOpenSubtasks ? T.amberBg : '#dcfce7'
              ),
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}>
              <Icon name="assignment" size={14} />
              {`${subtasks.length} subtaken${hasOpenSubtasks ? ' (nog open)' : ''}`}
            </Typography>
          </Box>
        )}

        {/* Notes Section */}
        {(opmerking || onUpdate) && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ ...stitchSectionLabel, mb: 1, display: 'block' }}>
              OPMERKING
            </Typography>
            <Box sx={{
              p: 2,
              backgroundColor: T.surfaceLow,
              borderRadius: '0.75rem',
              borderLeft: `3px solid ${T.outlineVariant}`
            }}>
              {onUpdate ? (
                <InlineTextEdit
                  value={opmerking}
                  multiline
                  placeholder="Klik om een opmerking toe te voegen"
                  onSave={(v) => handleFieldSave('Opmerking', v)}
                />
              ) : (
                <Typography variant="body2" sx={{ fontFamily: T.fontBody, whiteSpace: 'pre-wrap' }}>
                  {opmerking}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Created info */}
        {created && (
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, mt: 2, display: 'block' }}>
            Aangemaakt op {formatDate(created)}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${T.surfaceHigh}` }}>
        <Box
          component="a"
          href={`https://airtable.com/apphMT5j6gtUnMY1p/tblfs5bMNPZIwXJZY/${task.id}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ ...stitchOutlineBtn, mr: 'auto', fontSize: '0.75rem', textDecoration: 'none' }}
        >
          Airtable
        </Box>
        <WijzigingslogKnop entityId={task.id} titel={task.fields?.NaamTaak || ''} size={18} sx={{ mr: 'auto' }} />
        {isDraaidagTaak && (
          <Box component="span" onClick={naarCallsheet}
            sx={{ ...stitchOutlineBtn, fontSize: '0.75rem', color: '#b45309', borderColor: '#fcd34d' }}>
            📋 Callsheet maken
          </Box>
        )}
        {onDelete && (
          verwijderStap === 0 ? (
            <Box component="span" onClick={() => setVerwijderStap(1)}
              sx={{ ...stitchOutlineBtn, fontSize: '0.75rem', color: '#b91c1c', borderColor: '#fca5a5' }}>
              Verwijderen
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#b91c1c', fontWeight: 700 }}>Taak definitief verwijderen?</Typography>
              <Box component="span" onClick={() => setVerwijderStap(0)} sx={{ ...stitchOutlineBtn, fontSize: '0.75rem' }}>Nee</Box>
              <Box component="span" sx={{ ...stitchOutlineBtn, fontSize: '0.75rem', color: '#fff', bgcolor: '#b91c1c', borderColor: '#b91c1c', opacity: verwijderStap === 2 ? 0.6 : 1 }}
                onClick={async () => {
                  if (verwijderStap === 2) return;
                  setVerwijderStap(2);
                  try { await onDelete(task.id); onClose(); }
                  catch (e) { setVerwijderStap(0); }
                }}>
                {verwijderStap === 2 ? 'Bezig…' : 'Ja, verwijderen'}
              </Box>
            </Box>
          )
        )}
        <Box component="span" onClick={onClose} sx={stitchGradientBtn}>
          Gereed
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default TaskDetailDialog;
